import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Smartphone, Sparkles, Lock, ShoppingBag } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

const PRODUCTS = [
  {
    id: 1,
    name: "India Cricket Jersey 2025",
    type: "Official Kit",
    description: "BCCI-approved match jersey with moisture-wick fabric. Worn by your favourite players.",
    priceInr: "₹1,499",
    priceCoins: "2,500",
    image: "https://static.prod-images.emergentagent.com/jobs/d3d8e51b-58dc-49f0-8d05-e98008389fca/images/8bda1ea70785d405e5decbb3e3839c97c641bb0f730745f81eac4b9c3c033fe3.png",
    badge: "Bestseller",
    badgeColor: "#3B9EFF",
    tag: "CRICKET",
    tagColor: "#3B9EFF",
  },
  {
    id: 2,
    name: "Man City Home Kit 2025",
    type: "Limited Edition",
    description: "Authentic sky-blue home kit with premium build quality. Limited fan edition.",
    priceInr: "₹2,199",
    priceCoins: "3,500",
    image: "https://static.prod-images.emergentagent.com/jobs/d3d8e51b-58dc-49f0-8d05-e98008389fca/images/d047f71b283bc958e4f1b7b880c9088088b877a1741285490fbb9b57b6afe828.png",
    badge: "New Arrival",
    badgeColor: "#007AFF",
    tag: "FOOTBALL",
    tagColor: "#007AFF",
  },
  {
    id: 3,
    name: "Fanible Sports Cap",
    type: "Fan Exclusive",
    description: "Premium structured cap with embroidered Fanible logo. One size fits all.",
    priceInr: "₹699",
    priceCoins: "1,000",
    image: "https://static.prod-images.emergentagent.com/jobs/d3d8e51b-58dc-49f0-8d05-e98008389fca/images/54c9e47bd63cce031fb11c2b7c128b95c7da2c35f9d96ef6fae6d6b941714088.png",
    badge: "Exclusive",
    badgeColor: "#a855f7",
    tag: "MERCHANDISE",
    tagColor: "#a855f7",
  },
  {
    id: 4,
    name: "Cricket Bat Keychain",
    type: "Collector's Item",
    description: "Handcrafted miniature bat keychain in gold & walnut wood. A true fan collector's piece.",
    priceInr: "₹299",
    priceCoins: "500",
    image: "https://static.prod-images.emergentagent.com/jobs/d3d8e51b-58dc-49f0-8d05-e98008389fca/images/d75b2f3035db1a633279bf91a40292ec6ac13b1b7e54b10cb2d951d09d858790.png",
    badge: "Limited Stock",
    badgeColor: "#f59e0b",
    tag: "COLLECTIBLE",
    tagColor: "#f59e0b",
  },
];

function ProductCard({ product, onBuy, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay }}
      className="relative bg-[#141720] rounded-2xl overflow-hidden group flex flex-col hover:-translate-y-2 transition-transform duration-150"
      style={{ border: "1px solid rgba(255,255,255,0.08)" }}
    >
      {/* Animated colour top-strip */}
      <div
        className="absolute top-0 left-0 right-0 h-[2px] z-10 scale-x-0 opacity-0 group-hover:scale-x-100 group-hover:opacity-100 transition-all duration-200 origin-center"
        style={{ background: `linear-gradient(90deg, transparent, ${product.tagColor}, transparent)` }}
      />

      {/* Colour wash overlay on hover */}
      <div
        className="absolute inset-0 z-0 pointer-events-none rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-200"
        style={{ background: `radial-gradient(ellipse at top, ${product.tagColor}10 0%, transparent 65%)` }}
      />

      {/* Image */}
      <div className="relative h-56 bg-[#111419] overflow-hidden flex-shrink-0 z-[1]">
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#141720]/60 via-transparent to-transparent" />
        {/* Badge */}
        <div
          className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-[0.15em] px-2.5 py-1 rounded-full"
          style={{ background: `${product.badgeColor}25`, color: product.badgeColor, border: `1px solid ${product.badgeColor}60` }}
        >
          {product.badge}
        </div>
        {/* Sport tag */}
        <div className="absolute bottom-3 right-3">
          <span
            className="text-[9px] font-black uppercase tracking-[0.2em] px-2 py-0.5 rounded"
            style={{ background: "rgba(0,0,0,0.75)", color: product.tagColor, backdropFilter: "blur(4px)" }}
          >
            {product.tag}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-[1] flex flex-col flex-1 p-5">
        {/* Type */}
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#A1A1AA] mb-2">{product.type}</p>

        {/* Name */}
        <h3
          className="font-outfit font-bold text-white text-base leading-snug mb-2 transition-colors duration-150 group-hover:text-[color:var(--accent-color)]"
          style={{ '--accent-color': product.tagColor }}
        >
          {product.name}
        </h3>

        {/* Description */}
        <p className="text-[#A1A1AA] text-xs leading-relaxed mb-5 flex-1">{product.description}</p>

        {/* Price row */}
        <div className="flex items-end justify-between mb-4 pb-4 border-b border-white/6">
          <div>
            <p className="text-[10px] text-[#A1A1AA] uppercase tracking-wider mb-1">Price</p>
            <p className="font-outfit font-black text-2xl text-white">{product.priceInr}</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] text-[#A1A1AA] uppercase tracking-wider mb-1">or redeem</p>
            <div className="flex items-center gap-1.5">
              <Sparkles size={11} className="text-[#f59e0b]" />
              <span className="font-outfit font-bold text-sm text-[#f59e0b]">{product.priceCoins} Coins</span>
            </div>
          </div>
        </div>

        {/* CTA Button */}
        <button
          data-testid={`shop-buy-btn-${product.id}`}
          onClick={() => onBuy(product)}
          className="w-full flex items-center justify-center gap-2 font-bold text-sm uppercase tracking-wider rounded-xl py-3 hover:scale-[1.02] active:scale-[0.97] transition-transform duration-150 group/btn"
          style={{ background: product.tagColor, color: "#000" }}
        >
          Buy Now
          <ArrowRight size={15} className="group-hover/btn:translate-x-1 transition-transform duration-150" />
        </button>
      </div>
    </motion.div>
  );
}

export default function EShopSection() {
  const [modalProduct, setModalProduct] = useState(null);

  return (
    <section id="shop" data-testid="shop-section" className="py-24 lg:py-32 bg-[#0C0E13] relative">
      {/* Phase 2 locked overlay */}
      <div className="absolute inset-0 z-20 flex flex-col items-center justify-center pointer-events-none"
        style={{ background: "linear-gradient(to bottom, rgba(12,14,19,0) 0%, rgba(12,14,19,0.82) 30%, rgba(12,14,19,0.96) 55%, rgba(12,14,19,1) 100%)" }}>
        <div className="pointer-events-auto text-center px-6 mt-auto mb-20">
          <div className="inline-flex items-center gap-2 bg-[#f59e0b]/10 border border-[#f59e0b]/30 rounded-full px-4 py-2 mb-5">
            <Lock size={12} className="text-[#f59e0b]" />
            <span className="text-[10px] font-black uppercase tracking-[0.25em] text-[#f59e0b]">Coming Soon</span>
          </div>
          <h3 className="font-outfit font-black text-white text-3xl sm:text-4xl tracking-tight mb-3">
            The E-Shop is <span className="text-[#f59e0b]">Coming Soon</span>
          </h3>
          <p className="text-[#A1A1AA] text-sm max-w-md mx-auto mb-6 font-inter leading-relaxed">
            Official jerseys, fan merchandise, and exclusive collectibles — the full Fanible Shop unlocks soon after launch.
          </p>
          <a href="#download"
            className="inline-flex items-center gap-2 bg-[#f59e0b] text-black font-bold text-sm uppercase tracking-wider rounded-full px-7 py-3 hover:bg-white transition-colors duration-150">
            <ShoppingBag size={15} />
            Pre-Register to Get Notified
          </a>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 opacity-30 pointer-events-none select-none">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-16"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-px bg-[#f59e0b]" />
            <span className="text-xs uppercase tracking-[0.25em] font-bold text-[#f59e0b] font-outfit">E-Shop</span>
            <span className="flex items-center gap-1.5 bg-[#f59e0b]/10 border border-[#f59e0b]/30 rounded-full px-2.5 py-0.5">
              <Lock size={9} className="text-[#f59e0b]" />
              <span className="text-[9px] font-black uppercase tracking-wider text-[#f59e0b]">Coming Soon</span>
            </span>
          </div>
          <h2 className="font-outfit font-bold text-4xl sm:text-5xl tracking-tight text-white mb-4">
            Fan Gear &amp;<br />
            <span className="text-[#3B9EFF]">Collectibles</span>
          </h2>
          <p className="text-base md:text-lg text-[#A1A1AA] max-w-xl font-inter">
            Shop official jerseys, merchandise, and exclusive fan collectibles. Pay with cash or redeem your Fanible Coins.
          </p>
        </motion.div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PRODUCTS.map((product, i) => (
            <ProductCard
              key={product.id}
              product={product}
              delay={i * 0.1}
              onBuy={(p) => setModalProduct(p)}
            />
          ))}
        </div>
      </div>

      {/* Available in App Dialog */}
      <Dialog open={!!modalProduct} onOpenChange={() => setModalProduct(null)}>
        <DialogContent
          data-testid="shop-available-in-app-dialog"
          className="bg-[#171B24] border border-white/10 text-white max-w-sm rounded-2xl text-center"
        >
          <DialogHeader>
            <div className="w-16 h-16 rounded-2xl bg-[#3B9EFF]/10 flex items-center justify-center mx-auto mb-4">
              <Smartphone size={32} className="text-[#3B9EFF]" />
            </div>
            <DialogTitle className="text-white font-outfit font-bold text-xl">
              Available in App
            </DialogTitle>
            <DialogDescription className="sr-only">This item is available in the Fanible mobile app</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-[#A1A1AA] text-sm leading-relaxed">
              <span className="text-white font-semibold">{modalProduct?.name}</span> and the full Fanible Shop are available exclusively on the app.
            </p>
            <div className="bg-[#1C2030] rounded-xl p-4 flex items-center justify-between">
              <div className="text-left">
                <p className="text-[10px] text-[#A1A1AA] uppercase tracking-wider mb-0.5">Price</p>
                <p className="text-white font-outfit font-black text-xl">{modalProduct?.priceInr}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] text-[#A1A1AA] uppercase tracking-wider mb-0.5">or redeem</p>
                <div className="flex items-center gap-1 justify-end">
                  <Sparkles size={11} className="text-[#f59e0b]" />
                  <span className="font-outfit font-bold text-sm text-[#f59e0b]">{modalProduct?.priceCoins} Coins</span>
                </div>
              </div>
            </div>
            <a
              href="#download"
              data-testid="shop-download-btn"
              onClick={() => setModalProduct(null)}
              className="w-full flex items-center justify-center gap-2 bg-[#3B9EFF] text-black font-bold rounded-xl py-3.5 hover:bg-white transition-colors duration-300 text-sm uppercase tracking-wider"
            >
              Download App to Shop
              <ArrowRight size={16} />
            </a>
            <button
              onClick={() => setModalProduct(null)}
              className="text-xs text-[#A1A1AA] hover:text-white transition-colors"
            >
              Maybe later
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
